import { useMemo } from 'react'
import dayjs from 'date'
import localStorage from 'local-storage'
import { useForceUpdate } from 'hooks'
import { openModal } from 'modals'
import { getAnalyticsContext } from 'analytics'
import { useIntl } from 'intl'
import { constants, getAddItemProduct, processError } from 'helpers'

import { useInquire } from 'modules/payment'
import { useQueue, useQueueUpdateItem, useQueueDeleteItem } from 'modules/queue'

import { useModalWithLayers } from 'components/feedback'


type UseFirstProductsProps = {
  data: OrderItems.Subscription
  isThankYouPage?: boolean
}

type AvailableProduct = {
  type: 'postponed' | 'queue'
  product: QueueModule.AddItemProduct
  flatIndex?: number
  metadata?: QueueModule.Metadata
}

const useFirstProducts = ({ data, isThankYouPage }: UseFirstProductsProps) => {
  const { deliveryDate, plan } = data || {}
  const { productsPerPeriod, name: planName } = plan || {}

  const intl = useIntl()

  // ATTN if it's a thankyou page, we need to display the actual user's queue
  const { items: queueItems, isFetching: isQueueFetching, refetch } = useQueue(isThankYouPage ? undefined : { forPlan: planName })
  const [ queueUpdateItem, { isFetching: isUpdating } ] = useQueueUpdateItem()
  const [ queueDeleteItem, { isFetching: isDeleting } ] = useQueueDeleteItem()
  const inquireContext = useInquire()
  const reFetchInquire = inquireContext?.submit

  const modalWithLayersContext = useModalWithLayers()
  const openLayer = modalWithLayersContext?.openLayer

  const [ forceUpdateId, forceUpdate ] = useForceUpdate()

  const firstProducts = useMemo(() => {
    if (!deliveryDate || isQueueFetching) {
      return null
    }

    const availableProducts: AvailableProduct[] = []
    const isQueueAvailable = queueItems?.length > 0

    let firstMonthDeliveryDate = deliveryDate
    let isDisabled = false

    // if user is authorized and have a queue, we can use it
    if (isQueueAvailable) {
      const firstMonth = queueItems[0]
      firstMonthDeliveryDate = dayjs(new Date(firstMonth.year, firstMonth.month - 1, 1, 0, 0, 0, 0))
      isDisabled = firstMonth.stateOptions.isDisabled

      // add products from first queue month if they exits
      firstMonth.products
        .filter((item) => Boolean(item?.tradingItem?.productInfo))
        .forEach(({ flatIndex, tradingItem: { id, uid, isLimitedEdition, productInfo } }) => {
          availableProducts.push({
            type: 'queue',
            flatIndex,
            product: {
              ...productInfo,
              productId: productInfo.id,
              productUid: productInfo.uid,
              tradingItemId: id,
              tradingItemUid: uid,
              brand: productInfo.brandInfo.name,
              isLimitedEdition,
            },
          })
        })
    }

    // if current queue items is after delivery date, then we shouldn't display any product selection
    if (deliveryDate.isBefore(firstMonthDeliveryDate)) {
      return null
    }

    // there can be products in postponed items, they have higher priority than queue
    const postponedItems = localStorage.getItem<QueueModule.AddItemsInput>(constants.localStorageNames.queuePendingProducts)
    // first products to add
    const highPriorityProducts = localStorage.getItem<QueueModule.AddItemsInput>(constants.localStorageNames.queueHighPriorityProducts)
    // last products to add
    const lowPriorityProducts = localStorage.getItem<QueueModule.AddItemsInput>(constants.localStorageNames.queueLowPriorityProducts)

    if (postponedItems?.length) {
      const items = postponedItems
        .filter(({ product }) => Boolean(product))
        .map<AvailableProduct>(({ product, metadata }) => ({ type: 'postponed', product, metadata }))

      if (items.length) {
        availableProducts.unshift(...items)
      }
    }

    if (highPriorityProducts?.length) {
      const items = highPriorityProducts
        .filter(({ product }) => Boolean(product))
        .map<AvailableProduct>(({ product, metadata }) => ({ type: 'postponed', product, metadata }))

      if (items.length) {
        availableProducts.unshift(...items)
      }
    }

    if (lowPriorityProducts?.length) {
      const items = lowPriorityProducts
        .filter(({ product }) => Boolean(product))
        .map<AvailableProduct>(({ product, metadata }) => ({ type: 'postponed', product, metadata }))

      if (items.length) {
        availableProducts.push(...items)
      }
    }

    if (availableProducts.length) {
      // we should show all first products for the selected plan
      return new Array(productsPerPeriod).fill(null)
        .map((value, index) => {
          const availableProduct = availableProducts[index]
          const isEmpty = !availableProduct

          // on thank you page queue may be locked already
          const onEdit = isDisabled ? null : () => {
            const onContinue = async (selectedProduct: ProductFragment.Base) => {
              try {
                const selectedTradingItem = selectedProduct.tradingItems?.items?.[0]
                const selectedTradingItemUid = selectedTradingItem?.uid

                const { page, pageType } = getAnalyticsContext()
                const metadata = {
                  page,
                  pageType,
                  placement: 'First product popup',
                }

                // if selected product is already in the queue, we need to remove it first
                // otherwise user will get SAME_PERFUME error
                let existingQueueItem = null

                queueItems?.some(({ products }) => {
                  existingQueueItem = products?.find((item) => item?.tradingItem?.uid === selectedTradingItemUid)
                  return Boolean(existingQueueItem)
                })

                if (existingQueueItem) {
                  await queueDeleteItem({
                    index: existingQueueItem.flatIndex,
                    metadata,
                  })
                }

                if (availableProduct?.type === 'queue' && !selectedTradingItem?.isLimitedEdition) {
                  // for queue item and not LEP, just replace it and refetch our queue query
                  await queueUpdateItem({
                    index: availableProduct.flatIndex,
                    tradingItemUid: selectedTradingItemUid,
                    metadata,
                  })
                  await refetch()
                  if (typeof reFetchInquire === 'function') {
                    // We need to re-fetch whole inquire to correctly display promo products (PF-1519)
                    await reFetchInquire()
                  }
                }
                else {
                  // in other cases, we should split postponed products to first items and last items

                  // high-priority products are the first products in the queue
                  const newHighPriorityProducts = (highPriorityProducts || [])
                    .filter(({ product: { productUid } }) => productUid !== selectedProduct.uid && productUid !== availableProduct?.product.productUid)
                    .slice(0, productsPerPeriod)

                  newHighPriorityProducts.splice(
                    index < newHighPriorityProducts.length ? index : 0,
                    0,
                    {
                      position: 'FIRST',
                      metadata,
                      product: getAddItemProduct({ product: selectedProduct, tradingItem: selectedTradingItem, intl }),
                    }
                  )

                  // low-priority products will be added to the end of the queue
                  const newLowPriorityProducts = (lowPriorityProducts || postponedItems || [])
                    .filter(({ product: { productUid } }) => productUid !== selectedProduct.uid && productUid !== availableProduct?.product.productUid)

                  if (availableProduct?.product) {
                    newLowPriorityProducts.push({
                      position: 'LAST',
                      metadata: availableProduct?.metadata || metadata,
                      product: availableProduct?.product,
                    })
                  }

                  localStorage.setItem(constants.localStorageNames.queueHighPriorityProducts, newHighPriorityProducts)
                  localStorage.setItem(constants.localStorageNames.queueLowPriorityProducts, newLowPriorityProducts)
                  // ATTN when user select first products we replace queuePendingProducts with another items
                  localStorage.removeItem(constants.localStorageNames.queuePendingProducts)
                  forceUpdate()
                }
              }
              catch (error) {
                processError(error)
              }
            }

            if (openLayer) {
              openLayer('firstProduct', {
                selectedTradingItemUid: availableProduct?.product.tradingItemUid,
                onContinue,
              })
            }
            else {
              openModal('firstProductModal', {
                selectedTradingItemUid: availableProduct?.product.tradingItemUid,
                onContinue,
              })
            }
          }

          return {
            flatIndex: index,
            product: availableProducts[index]?.product,
            isEmpty,
            onEdit,
          }
        })
    }

    return null
  }, [ isQueueFetching, deliveryDate, queueItems, productsPerPeriod, openLayer, queueUpdateItem, queueDeleteItem, forceUpdate, refetch, reFetchInquire, forceUpdateId, intl ])

  // TODO implement edit functionality - added on 2021-09-27 by maddoger
  return {
    firstProducts,
    isFetching: isQueueFetching,
    isSubmitting: isUpdating || isDeleting,
  }
}


export default useFirstProducts
